import * as _ from "lodash";
import { Options, RRule, Weekday } from "rrule";
import { IDateRange, IMessage, MessageType } from "../PA.Core.IF.Basic";
import { TextUtils } from "./Text";
import { WB_ScheduleRow } from "../PAdbSchemaRows";
import { MessageProvider } from "./Messages";
import { ErrorMessage_restate } from "./MessageUtils";

const SAPBW_Date_Undefined: string = "00000000"
const SAPBW_DATE_MAXDATE = "99991231";
const SAPBW_DATE_MINDATE = "18000101";

// Time Format HHmmSS  UTC
const TIME_UNDEFINED = "00000"

const dctDayNames: { [id: number]: string } = {
    1: "Sunday",
    2: "Monday",
    3: "Tuesday",
    4: "Wednesday",
    5: "Thursday",
    6: "Friday",
    7: "Saturday"
};

export class DateUtils {


    static RangeDatesOverlap(aStartDate1: Date, anEndDate1: Date,
                             aStartDate2: Date, anEndDate2: Date): boolean {

        if (anEndDate1 < aStartDate2)
            return false;

        if (anEndDate2 < aStartDate1)
            return false;

        return true;
    }

    static RangesOverlap(a: IDateRange,
                         b: IDateRange): boolean {
        return DateUtils.RangeDatesOverlap(a.StartDt, a.EndDt, b.StartDt, b.EndDt)
    }


    static InRange(aDate: Date, aRange: IDateRange): boolean {
        return DateUtils.InRangeDates(aDate, aRange.StartDt, aRange.EndDt)
    }

    static InRangeDates(aDate: Date, aStartDate: Date, anEndDate: Date): boolean {
        return ((aDate >= aStartDate) && (aDate <= anEndDate));
    }


    static Greatest(aDate1?: Date, aDate2?: Date): Date {
        if (!_.isDate(aDate1))
            return aDate2;

        if (!_.isDate(aDate2))
            return aDate1;

        if (aDate1 > aDate2)
            return aDate1;
        else
            return aDate2;
    }

    static Least(aDate1?: Date, aDate2?: Date): Date {
        if (!_.isDate(aDate1))
            return aDate2;

        if (!_.isDate(aDate2))
            return aDate1;

        if (aDate1 < aDate2)
            return aDate1;
        else
            return aDate2;
    }

    static RangeDatesUnion(aStartDate: Date, anEndDate: Date, newDate: Date): IDateRange {
        return {
            StartDt: DateUtils.Least(aStartDate, newDate),
            EndDt: DateUtils.Greatest(anEndDate, newDate)
        }
    }

    static RangeUnion(a: IDateRange, b: IDateRange): IDateRange {
        return {
            StartDt: DateUtils.Least(a.StartDt, b.StartDt),
            EndDt: DateUtils.Greatest(a.EndDt, b.EndDt)
        }
    }


    static RangeIntersect(a: IDateRange, b: IDateRange): IDateRange {
        return DateUtils.RangeDatesIntersect(a.StartDt, a.EndDt, b.StartDt, b.EndDt)
    }

    static RangeDatesIntersect(A_StartDate: Date, A_EndDate: Date,
                               B_StartDate: Date, B_EndDate: Date): IDateRange {

        const emptyRange: IDateRange = {StartDt: undefined, EndDt: undefined}

        try {
            // if one of the ranges is null, then there is no overlap
            if ((!_.isDate(A_StartDate)) && (!_.isDate(A_EndDate))) {
                return emptyRange
            }
            if ((!_.isDate(B_StartDate)) && (!_.isDate(B_EndDate))) {
                return emptyRange
            }

            let int_Start = DateUtils.Greatest(A_StartDate || A_EndDate, B_StartDate || B_EndDate);
            let int_End = DateUtils.Least(A_EndDate || A_StartDate, B_EndDate || B_StartDate);

            if (int_Start > int_End) {
                return emptyRange
            }

            return {StartDt: int_Start, EndDt: int_End}

        } catch (err) {
        }

        return emptyRange

    }


    static RangeDaysOverlap(a: IDateRange, b: IDateRange): number {
        return DateUtils.RangeDatesDaysOverlap(a.StartDt, a.EndDt, b.StartDt, b.EndDt)
    }

    static RangeDatesDaysOverlap(A_StartDate: Date, A_EndDate: Date,
                                 B_StartDate: Date, B_EndDate: Date): number {

        let overlapPeriod = DateUtils.RangeDatesIntersect(A_StartDate, A_EndDate, B_StartDate, B_EndDate)
        if (_.isUndefined(overlapPeriod.StartDt) || _.isUndefined(overlapPeriod.EndDt)) {
            return 0
        }

        return DateUtils.durationInDays(overlapPeriod)

    }


    static convertToSAPBW(aDate: Date): string {

        if (!_.isDate(aDate)) {
            return SAPBW_Date_Undefined
        }

        var dd: string = aDate.getDate() + ''
        var mm: string = (aDate.getMonth() + 1) + ''
        var yyyy: string = aDate.getFullYear() + ''

        dd = TextUtils.padStart(dd, 2, '0')
        mm = TextUtils.padStart(mm, 2, '0')
        yyyy = TextUtils.padStart(yyyy, 4, '0')

        return yyyy + mm + dd
    }

    static isSAPBWDateFormat(aDate: string): boolean {
        // Regular expression to match the "YYYYMMDD" format
        const regex = /^\d{4}\d{2}\d{2}$/;
        return regex.test(aDate);
    }

    static convertFromSAPBW(aBwString: string): Date {
        if (_.isEmpty(aBwString))
            return undefined

        if (aBwString == SAPBW_Date_Undefined)
            return undefined

        if (aBwString.length != 8)
            return undefined

        var yyyy = Number(aBwString.substring(0, 4))
        var mm = Number(aBwString.substring(4, 6)) - 1
        var dd = Number(aBwString.substring(6, 8))

        return new Date(yyyy, mm, dd)
    }

    // let('2022-01-06T05:00:00.000Z')
    static convertFromISOIgnoreTZ(aISOString: string): Date {
        if (_.isEmpty(aISOString))
            return undefined

        if (aISOString.length < 10)
            return undefined

        var yyyy = Number(aISOString.substring(0, 4))
        var mm = Number(aISOString.substring(5, 7)) - 1
        var dd = Number(aISOString.substring(8, 10))

        return new Date(yyyy, mm, dd)
    }

    static convertFromSAPBWToReadable(aBwString: string, locale?: string): string {
        if (_.isEmpty(aBwString))
            return undefined

        if (aBwString == SAPBW_Date_Undefined)
            return undefined

        if (aBwString.length != 8)
            return undefined

        var yyyy = Number(aBwString.substring(0, 4))
        var mm: any = Number(aBwString.substring(4, 6))
        var dd: any = Number(aBwString.substring(6, 8))

        if (mm < 10)
            mm = "0" + mm.toString()

        if (dd < 10)
            dd = "0" + dd.toString()


        if (locale == "us")
            return `${mm}/${dd}/${yyyy}`
        return `${yyyy}/${mm}/${dd}`
    }

    static convertToSAPBWTs(aDate: Date): number {

        if (!_.isDate(aDate)) {
            return 0
        }

        var yyyy: number = aDate.getUTCFullYear() * 10000000000
        var mm: number = (aDate.getUTCMonth() + 1) * 100000000
        var dd: number = aDate.getUTCDate() * 1000000
        let hrs: number = aDate.getUTCHours() * 10000
        let mins: number = aDate.getUTCMinutes() * 100
        let secs: number = aDate.getUTCSeconds()

        return yyyy + mm + dd + hrs + mins + secs

    }

    /** Returns a date that is the local time, converted from aBwNumber, which is read in as UTC */
    static convertFromSAPBWTs(aBwNumber: number): Date {

        var yyyy: number = Math.floor(aBwNumber / 10000000000) % 10000
        var mm: number = (Math.floor(aBwNumber / 100000000) % 100) - 1
        var dd: number = Math.floor(aBwNumber / 1000000) % 100
        let hrs: number = Math.floor(aBwNumber / 10000) % 100
        let mins: number = Math.floor(aBwNumber / 100) % 100
        let secs: number = aBwNumber % 100

        return new Date(Date.UTC(yyyy, mm, dd, hrs, mins, secs))

    }

    /** Returns a date created from the date parts in aBwNumber. Ignores any UTC or timezones,
     * so the returned Date will be the local timezone */
    static convertFromSAPBWTsNoUTC(aBwNumber: number): Date {

        var yyyy: number = Math.floor(aBwNumber / 10000000000) % 10000
        var mm: number = (Math.floor(aBwNumber / 100000000) % 100) - 1
        var dd: number = Math.floor(aBwNumber / 1000000) % 100
        let hrs: number = Math.floor(aBwNumber / 10000) % 100
        let mins: number = Math.floor(aBwNumber / 100) % 100
        let secs: number = aBwNumber % 100

        return new Date(yyyy, mm, dd, hrs, mins, secs)

    }


    static convertFromSAPBWDateTimeSplit(aBwDtString: string, aBwTmString: string): Date {

        if (_.isEmpty(aBwDtString))
            return undefined

        if (aBwDtString == SAPBW_Date_Undefined)
            return undefined

        if (aBwDtString.length != 8)
            return undefined

        let yyyy = Number(aBwDtString.substring(0, 4))
        let mm = Number(aBwDtString.substring(4, 6)) - 1
        let dd = Number(aBwDtString.substring(6, 8))


        let hrs = 0
        let mins = 0
        let secs = 0

        if (_.isEmpty(aBwTmString) || (aBwTmString == TIME_UNDEFINED) || (aBwTmString.length != 6)) {
            // already set to zero
        } else {
            hrs = _.toNumber(aBwTmString.substring(0, 2))
            mins = _.toNumber(aBwTmString.substring(2, 4))
            secs = _.toNumber(aBwTmString.substring(4, 6))
        }

        return new Date(Date.UTC(yyyy, mm, dd, hrs, mins, secs))
    }


    static convertToSAPBWDateTimeSplit(aDate: Date): { Dt: string, Tm: string } {

        if (!_.isDate(aDate)) {
            return {Dt: SAPBW_Date_Undefined, Tm: TIME_UNDEFINED}
        }

        let dd = TextUtils.padStart(aDate.getUTCDate() + '', 2, '0')
        let mm = TextUtils.padStart((aDate.getUTCMonth() + 1) + '', 2, '0')
        let yyyy = TextUtils.padStart(aDate.getUTCFullYear() + '', 4, '0')

        let hrs = TextUtils.padStart(aDate.getUTCHours() + '', 2, '0')
        let mins = TextUtils.padStart(aDate.getUTCMinutes() + '', 2, '0')
        let secs = TextUtils.padStart(aDate.getUTCSeconds() + '', 2, '0')

        return {Dt: [yyyy, mm, dd].join(""), Tm: [hrs, mins, secs].join("")}
    }

    static convertToSAPBWDateTimeToUTCString(aDate: number): string {
        let dateTime = DateUtils.convertFromSAPBWTs(aDate);
        let dateTimeSplit = DateUtils.convertToSAPBWDateTimeSplit(dateTime);
        let dateString = dateTimeSplit.Dt;
        let timeString = dateTimeSplit.Tm;

        return dateString.substring(0, 4) + "-" + dateString.substring(4, 6) + "-" + dateString.substring(6) + " " +
            timeString.substring(0, 2) + ":" + timeString.substring(2, 4) + ":" + timeString.substring(4) +
            " UTC";

    }

    /**
     *  Return a Date object parsed from a string of format yyyy-mm-dd
     **/
    static convertFromSF(aDate: string): Date {
        if (_.isEmpty(aDate))
            return undefined

        if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(aDate))
            return undefined

        let yyyy = Number(aDate.substring(0, 4))
        let mm = Number(aDate.substring(5, 7)) - 1
        let dd = Number(aDate.substring(8, 10))

        return new Date(yyyy, mm, dd)
    }

    static get today(): Date {
        var returnValue = new Date()
        returnValue.setHours(0, 0, 0, 0)
        return returnValue
    }

    static convertToSF(aDate: Date): string {
        if (!_.isDate(aDate)) {
            return undefined
        }

        var dd: string = aDate.getDate() + ''
        var mm: string = (aDate.getMonth() + 1) + ''
        var yyyy: string = aDate.getFullYear() + ''

        dd = TextUtils.padStart(dd, 2, '0')
        mm = TextUtils.padStart(mm, 2, '0')
        yyyy = TextUtils.padStart(yyyy, 4, '0')

        return `${yyyy}-${mm}-${dd}`
    }

    static addDays(aDate: Date, numDays: number): Date {
        if (!_.isDate(aDate)) {
            return aDate
        }
        let result = new Date(aDate.getTime());
        result.setDate(result.getDate() + numDays);
        return result;
    }

    static subtractDays(aDate: Date, numDays: number): Date {
        if (!_.isDate(aDate)) {
            return aDate
        }
        let result = new Date(aDate.getTime());
        result.setDate(result.getDate() - numDays);
        return result;
    }

    static lengthInDays(aRange: IDateRange): number {
        return DateUtils.durationInDays(aRange)
    }

    static durationInDays(aRange: IDateRange): number {
        if (_.isEmpty(aRange)) {
            return 0
        }
        return Math.round(Math.abs(DateUtils.differenceInDays(aRange.StartDt, aRange.EndDt))) + 1
    }

    static differenceInDays(aDate: Date, bDate: Date): number {
        if (!_.isDate(aDate)) {
            return 0
        }
        if (!_.isDate(bDate)) {
            return 0
        }

        var ONE_DAY = 1000 * 60 * 60 * 24
        // Convert both dates to milliseconds
        var date1_ms = bDate.getTime()
        var date2_ms = aDate.getTime()
        // Calculate the difference in milliseconds
        var difference_ms = date1_ms - date2_ms
        // Convert back to days and return
        return (difference_ms / ONE_DAY)
    }

    static differenceInWeeks(aDate: Date, bDate: Date): number {
        if (!_.isDate(aDate)) {
            return 0
        }
        if (!_.isDate(bDate)) {
            return 0
        }

        return Math.round(DateUtils.durationInDays({StartDt: aDate, EndDt: bDate}) / 7);
    }


    static isLessThan(aDate: Date, bDate: Date): boolean {
        let bDifferenceDays = DateUtils.differenceInDays(aDate, bDate)
        return bDifferenceDays > 0
    }

    /**
     * Returns true if aDate is less than or equal to bDate
     **/
    static isLessThanEqual(aDate: Date, bDate: Date): boolean {
        let bDifferenceDays = DateUtils.differenceInDays(aDate, bDate)
        return bDifferenceDays >= 0
    }

    /**
     * Returns true is aDate is greater than bDate
     * @param aDate
     * @param bDate
     */
    static isGreaterThan(aDate: Date, bDate: Date): boolean {
        let bDifferenceDays = DateUtils.differenceInDays(aDate, bDate)
        return bDifferenceDays < 0
    }

    static isGreaterThanEqual(aDate: Date, bDate: Date): boolean {
        let bDifferenceDays = DateUtils.differenceInDays(aDate, bDate)
        return bDifferenceDays <= 0
    }

    static isEqual(aDate: Date, bDate: Date): boolean {
        let bDifferenceDays = DateUtils.differenceInDays(aDate, bDate)
        return Math.round(bDifferenceDays) == 0
    }


    static coalesce(aRange: IDateRange, bRange: IDateRange): IDateRange {
        if (_.isEmpty(aRange)) {
            return bRange
        }

        if (_.isEmpty(bRange)) {
            return aRange
        }

        return {StartDt: aRange.StartDt || bRange.StartDt, EndDt: aRange.EndDt || bRange.EndDt}

    }

    static getMaxDate(): Date {
        return DateUtils.convertFromSAPBW(SAPBW_DATE_MAXDATE);
    }

    static getMinDate(): Date {
        return DateUtils.convertFromSAPBW(SAPBW_DATE_MINDATE);
    }

    static getMaxSAPBWDate(): string {
        return SAPBW_DATE_MAXDATE;
    }

    static getMinSAPBWDate(): string {
        return SAPBW_DATE_MINDATE;
    }

    static formatDate(aDate: Date): string {
        if (_.isDate(aDate)) {
            return aDate.toLocaleDateString()
        }

        return "-"
    }

    /** Returns a locale-formatted date string. If excludeSeconds is true, then the seconds will not be included */
    static formatDateTime(aDate: Date, excludeSeconds?: boolean): string {
        if (!_.isDate(aDate)) {
        return "-"
        }

        if(excludeSeconds) {
            return aDate.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            })
        }

        return aDate.toLocaleDateString() + ' ' + aDate.toLocaleTimeString()
    }

    static getDayName(aDate: Date): string {
        return dctDayNames[aDate.getDay() + 1];
    }

    static formatRange(aRange: IDateRange, sSep: string = ' - '): string {
        return [DateUtils.formatDate(aRange.StartDt), DateUtils.formatDate(aRange.EndDt)].join(sSep)
    }

    static createDateRange(startDt: Date, endDt: Date): IDateRange {
        return {
            StartDt: startDt,
            EndDt: endDt
        }
    }

    static createDateRangeSAPBW(startDt: string, endDt: string): IDateRange {
        return DateUtils.createDateRange(DateUtils.convertFromSAPBW(startDt), DateUtils.convertFromSAPBW(endDt))
    }

    static isTimeWithinWindow_HrMin(timeToCheck: Date, window: IDateRange): boolean {

        let timeToCheckMins = timeToCheck.getMinutes() + timeToCheck.getHours() * 60;
        let startRangeMins = window.StartDt.getMinutes() + window.StartDt.getHours() * 60;
        let endRangeMins = window.EndDt.getMinutes() + window.EndDt.getHours() * 60;

        return timeToCheckMins <= endRangeMins && timeToCheckMins >= startRangeMins;
    }

    static convertFromSAPBWDateToString(aBwDtString: string, aBwTmString: string): string {
        if (_.isEmpty(aBwDtString))
            return undefined

        if (aBwDtString == SAPBW_Date_Undefined)
            return undefined

        if (aBwDtString.length != 8)
            return undefined

        let yyyy = Number(aBwDtString.substring(0, 4))
        let mm = Number(aBwDtString.substring(4, 6)) - 1
        let dd = Number(aBwDtString.substring(6, 8))


        let hrs = 0
        let mins = 0
        let secs = 0

        if (_.isEmpty(aBwTmString) || (aBwTmString == TIME_UNDEFINED) || (aBwTmString.length != 6)) {
            // already set to zero
        } else {
            hrs = _.toNumber(aBwTmString.substring(0, 2))
            mins = _.toNumber(aBwTmString.substring(2, 4))
            secs = _.toNumber(aBwTmString.substring(4, 6))
        }

        return new Date(Date.UTC(yyyy, mm, dd, hrs, mins, secs)).toLocaleDateString()
    }


    static convertFromSAPBWTimeToString(aBwTmString: string): string {

        let hrs = 0
        let mins = 0
        let secs = 0

        hrs = _.toNumber(aBwTmString.substring(0, 2))
        mins = _.toNumber(aBwTmString.substring(2, 4))
        secs = _.toNumber(aBwTmString.substring(4, 6))

        return new Date(Date.UTC(hrs, mins, secs)).toTimeString()
    }

    static convertToSAPBWDateToString(aDate: Date): { Dt: string } {

        if (!_.isDate(aDate)) {
            return {Dt: SAPBW_Date_Undefined}
        }

        let dd = TextUtils.padStart(aDate.getUTCDate() + '', 2, '0')
        let mm = TextUtils.padStart((aDate.getUTCMonth() + 1) + '', 2, '0')
        let yyyy = TextUtils.padStart(aDate.getUTCFullYear() + '', 4, '0')
        return {Dt: [yyyy, mm, dd].join("")}
    }

    static convertToLocalTS(aDate: Date): any {

        if (!_.isDate(aDate)) {
            return ''
        }

        var yyyy: number = aDate.getFullYear()
        var mm: number = (aDate.getMonth() + 1)
        var dd: number = aDate.getDate()
        let hrs: number = aDate.getHours()
        let mins: number = aDate.getMinutes()
        let secs: number = aDate.getSeconds()

        // return yyyy + mm + dd + hrs + mins + secs
        return `${mm}/${dd}/${yyyy} ${hrs}:${mins}:${secs}`


    }

    /**
     * Takes the given date and returns a string YYYYYmmddHH in UTC
     **/
    static convertToRunId(aDate: Date): string {
        if (!_.isDate(aDate)) {
            return ''
        }

        var yyyy: string = TextUtils.padStart(aDate.getUTCFullYear() + '', 2, '0');
        var mm: string = TextUtils.padStart((aDate.getUTCMonth() + 1) + '', 2, '0');
        var dd: string = TextUtils.padStart(aDate.getUTCDate() + '', 2, '0');
        let hrs: string = TextUtils.padStart(aDate.getUTCHours() + '', 2, '0');
        let mins: string = TextUtils.padStart(aDate.getUTCMinutes() + '', 2, '0');

        return `${yyyy}${mm}${dd}${hrs}${mins}`
    }

    /**
     @param runId String of the format YYYYmmddHH in UTC
     **/
    static convertFromRunId(runId: string): Date {
        if (_.isEmpty(runId)) {
            return undefined;
        }

        let yyyy = Number(runId.substring(0, 4))
        let mm = Number(runId.substring(4, 6)) - 1
        let dd = Number(runId.substring(6, 8))


        let hrs = Number(runId.substring(8, 10))
        let mins = Number(runId.substring(10, 12))
        return new Date(Date.UTC(yyyy, mm, dd, hrs, mins, 0));
    }

    /**
     * Returns a date range of the calendar month containing the given date
     **/
    static getMonthDateRange(aDate: Date): IDateRange {
        return {
            StartDt: new Date(aDate.getFullYear(), aDate.getMonth(), 1),
            EndDt: new Date(aDate.getFullYear(), aDate.getMonth() + 1, 0)
        }
    }

    /**
     * Returns a date range of the calendar month containing the given date
     * @param aBWDate A date in the format yyyymmdd
     **/
    static getMonthDateRangeBW(aBWDate: string): IDateRange {
        return DateUtils.getMonthDateRange(DateUtils.convertFromSAPBW(aBWDate));
    }

    static fitRangeToStartDOW(src: IDateRange, tgtDOW: number): IDateRange {
        //--
        if(!_.isDate(src?.StartDt) || (tgtDOW > 7) || (tgtDOW < 1))
            return _.clone(src)

        //--( 0-IF(PromoStartDOW-ActualStartDOW>=4,PromoStartDOW-ActualStartDOW-7,PromoStartDOW-ActualStartDOW)
        let srcStartDOW = src.StartDt.getDay() + 1

        if(srcStartDOW == tgtDOW)
            return _.clone(src)

        let daysToMoveDirection = DateUtils.DayOfWeek_findNearestOffset(srcStartDOW, tgtDOW)

        return {StartDt: DateUtils.addDays(src.StartDt, daysToMoveDirection), EndDt: DateUtils.addDays(src.EndDt, daysToMoveDirection)}

    }

    static DayOfWeek_findNearestOffset(curDOW: number, tgtDOW: number): number {
        if (curDOW == tgtDOW)
            return 0

        let daysDiff = tgtDOW - curDOW
        // diff should range from -6 to 6
        //   the closest week should not be more than +/- 3 days away

        let daysToMoveDirection = daysDiff
        if (daysDiff >= 4)
            daysToMoveDirection = daysDiff - 7
        if (daysDiff <= -4)
            daysToMoveDirection = 7 + daysDiff
        return daysToMoveDirection
    }

    static Range_addDays(src: IDateRange, days: number): IDateRange {
        return { StartDt: DateUtils.addDays(src?.StartDt, days), EndDt: DateUtils.addDays(src?.EndDt, days) }
    }

    static Range_fitToFullWeek(src: IDateRange): IDateRange {
        if(!_.isDate(src?.StartDt) || !_.isDate(src?.EndDt))
            return src

        let duration = DateUtils.durationInDays(src)
        if(duration > 0){
            let weekCount = Math.ceil(duration/7)
            return {StartDt: src.StartDt, EndDt: DateUtils.addDays(src.StartDt, ((weekCount * 7) - 1)) }
        }
        return src
    }

}


export class RRuleUtils {

    static getRuleOption(row: WB_ScheduleRow): Partial<Options> {
        const dctWeekDayByDay: {[id:string]: Weekday} = {
            Monday: RRule.MO,
            Tuesday: RRule.TU,
            Wednesday: RRule.WE,
            Thursday: RRule.TH,
            Friday: RRule.FR,
            Saturday: RRule.SA,
            Sunday: RRule.SU
        }

        let frequency = undefined
        const ruleOpt: Partial<Options> = {
            count: 1, // limits to one occurrence.. why ?
            interval: 1,
            dtstart : row.ScheduleStartDtTs,
            until: row.ScheduleEndDtTs
        }
        let bApplyYearDays = false
        let bApplyMonthDays: boolean = false
        let bApplyWeekDays: boolean = false

        switch (row.cif.RULE_FREQUENCY) {
            case 'yearly':
                frequency = RRule.YEARLY
                if (!_.isEmpty(row.cif.RULE_YEAR_DAYS))
                    bApplyYearDays = true
                break;
            case 'monthly':
                frequency = RRule.MONTHLY
                if (!_.isEmpty(row.cif.RULE_MONTH_DAYS))
                    bApplyMonthDays = true
                if (!_.isEmpty(row.cif.RULE_WEEK_DAYS))
                    bApplyWeekDays = true
                break;
            case'weekly':
                frequency = RRule.WEEKLY
                if (!_.isEmpty(row.cif.RULE_WEEK_DAYS))
                    bApplyWeekDays = true
                break;
            case 'daily':
                frequency = RRule.DAILY
                break;
            case 'hourly':
                frequency = RRule.HOURLY
                break
        }

        if (bApplyYearDays) {
            if (!("byyearday" in ruleOpt))
                ruleOpt.byyearday = [];
            const days = row.cif.RULE_YEAR_DAYS.match(/\d+/g);
            ruleOpt.byyearday = _.map(days, day => Number(day));
        }

        if (bApplyMonthDays) {
            if (!("bymonthday" in ruleOpt))
                ruleOpt["bymonthday"] = [];
            const days = row.cif.RULE_MONTH_DAYS.match(/\d+/g);
            ruleOpt.bymonthday = _.map(days, day => Number(day));
        }

        if (bApplyWeekDays) {
            const days = row.cif.RULE_WEEK_DAYS.split(",");
            if (!("byweekday" in ruleOpt))
                ruleOpt.byweekday = _.map(days, day => dctWeekDayByDay[day]);

        }

        ruleOpt.freq = frequency
        return ruleOpt
    }

    static getRuleText(row: WB_ScheduleRow): string {
        if (_.isEmpty(row))
            return ""

        if (row.cif.SCHEDULE_TYPE == 'once') {
            return "Once on " + DateUtils.convertFromSAPBWToReadable(row.cif.SCHEDULE_START_DTTS.toString().substring(0, 8), 'us')
        }

        let message = "Every "
        if (!this.isValidRule(row)) {
            return ""
        } else {
            function getLabel(day: string) {
                let d = ""
                switch (Number(day)) {
                    case 1:
                        d = day + "st"
                        break;
                    case 2:
                        d = day + "nd"
                        break;
                    case 3:
                        d = day + "rd"
                        break;
                    default:
                        d = day + "th"
                }
                return d
            }

            switch (row.cif.RULE_FREQUENCY) {
                case 'yearly':
                    message += "year "
                    let days = row.cif.RULE_YEAR_DAYS.match(/\d+/g)
                    message += "on " + _.join(_.map(days, day => {
                        return getLabel(day)
                    }), ', ')

                    break;
                case 'monthly':
                    message += "month "
                    let monthday: string []
                    let weekday: string[]

                    if (!_.isEmpty(row.cif.RULE_MONTH_DAYS) && _.isEmpty(row.cif.RULE_WEEK_DAYS)) {
                        monthday = row.cif.RULE_MONTH_DAYS.match(/\d+/g)
                        message += "on " + _.join(_.map(monthday, day => {
                            return getLabel(day)
                        }), ', ')
                    }

                    if (!_.isEmpty(row.cif.RULE_WEEK_DAYS) && _.isEmpty(row.cif.RULE_MONTH_DAYS)) {
                        message += "on " + row.cif.RULE_WEEK_DAYS
                    }


                    if (!_.isEmpty(row.cif.RULE_MONTH_DAYS) && !_.isEmpty(row.cif.RULE_WEEK_DAYS)) {
                        monthday = row.cif.RULE_MONTH_DAYS.match(/\d+/g)
                        weekday = row.cif.RULE_WEEK_DAYS.split(",")
                        message += "on " + _.join(weekday, " or ")
                        message += " " + _.join(_.map(monthday, day => {
                            return getLabel(day)
                        }))
                    }

                    if (row.cif.RULE_MONTH_LAST_DAY_IND == '1') {
                        if (!_.isEmpty(row.cif.RULE_MONTH_DAYS) || !_.isEmpty(row.cif.RULE_WEEK_DAYS) )
                            message += ', '
                        message += "Last day of a month"
                    }
                    break;
                case'weekly':
                    message += "week "
                    if (!_.isEmpty(row.cif.RULE_WEEK_DAYS)) {
                        message += "on " + row.cif.RULE_WEEK_DAYS
                    }
                    break;
                case 'daily':
                    message += "day "
                    break;
                case 'hourly':
                    message += "hour "
                    break
            }

            return message

        }

    }
    /**
     * @deprecated The method should not be used
     */
    static isValidRule(row: WB_ScheduleRow): boolean {
        let msgs = RRuleUtils.getValidationMessages(row)
        let errMsg = _.filter(msgs, (a) => a.type == MessageType.Error)
        return errMsg.length === 0
    }

    static getValidationMessages(row: WB_ScheduleRow, hasTrigger?: boolean): IMessage[] {
        if (_.isEmpty(row))
            return []

        const bHasTrigger = !!hasTrigger
        const bHasYearDays = !_.isEmpty(row.cif.RULE_YEAR_DAYS)
        const bHasMonthDays = !_.isEmpty(row.cif.RULE_MONTHS)
        const bHasWeekDays = !_.isEmpty(row.cif.RULE_WEEK_DAYS)

        let mp = new MessageProvider()

        switch(row.cif.SCHEDULE_TYPE){
            case 'once': {
                if (bHasTrigger)
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_START_DTTS == 0, 'No Start Time', 'No Start Time')
                else
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_START_DTTS == 0, 'No Start Date', 'No Start Time')

                const OriginalStartDtTs = row.previous("SCHEDULE_START_DTTS") || 0
                if (row.isActiveNew || (OriginalStartDtTs == 0)) {
                    //Check if the start date is in the future
                    if (!bHasTrigger) {
                        const dtsNow = DateUtils.convertToSAPBWTs(new Date())
                        ErrorMessage_restate(mp, row.cif.SCHEDULE_START_DTTS < dtsNow, 'StartDateInvalid', 'Start Date/Time must be in the future')
                    }
                }
            }
            break;


            case 'now':
                // nothing to evaluate
                return []

            case 'recurring': {
                // Start Date/ End Date is required for all recurring schedules

                switch (row.cif.RULE_FREQUENCY) {
                    case 'yearly':
                        // You can only select days of Year, but it's not required
                        break;

                    case 'monthly':
                        // You can select days of Month, but it's not required
                        // You can select days of Week, but it's not required
                        break;

                    case 'weekly':
                        // You can select days of Month, but it's not required
                        // You can select days of Week, but it's not required
                        break;

                    case 'hourly':
                    case 'daily':
                        break;
                    default:
                        ErrorMessage_restate(mp, true, 'No Frequency', 'No Recurring Frequency Selected')
                }
                if(bHasTrigger) {
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_START_DTTS == 0, 'StartDtNotSet', 'Start Date not set')
                   // ErrorMessage_restate(mp, row.cif.SCHEDULE_END_DTTS == 0, 'EndDtNotSet', 'End Date not set')
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_END_DTTS != 0 && row.cif.SCHEDULE_END_DTTS < row.cif.SCHEDULE_START_DTTS, 'EndDtNotValid', 'Start Date after End Date')
                } else {
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_START_DTTS == 0, 'StartDtNotSet', 'Start Date/ Time not set')
                  //  ErrorMessage_restate(mp, row.cif.SCHEDULE_END_DTTS == 0, 'EndDtNotSet', 'End Date/ Time not set')
                    ErrorMessage_restate(mp, row.cif.SCHEDULE_END_DTTS != 0 && row.cif.SCHEDULE_END_DTTS < row.cif.SCHEDULE_START_DTTS, 'EndDtNotValid', 'Start Date/ Time after End Date/ Time')
                }

                break;
            }
        }


        return mp.Messages
    }

    /**
     * Gets All Dates only for recurrent Schedules
     */
    static getRules(row: WB_ScheduleRow): Date[] {
        let ruleOpt = this.getRuleOption(row)
        if (_.isUndefined(ruleOpt.freq))
            return []

        let rule = new RRule(ruleOpt)
        let rules = rule.all()
        return rules
    }

    /**
     * Get Recurring Rule dates between two dates
     */
    static getRulesDateBetween(row: WB_ScheduleRow, startDt: Date, endDt: Date, limit?: number): Date[] {
        let l = limit || 0
        let ruleOpt = this.getRuleOption(row)
        if (_.isUndefined(ruleOpt.freq))
            return []
        if(limit > 0)
            ruleOpt.count = limit
        else
            delete ruleOpt.count

        let rule = new RRule(ruleOpt)
        console.log(new Date(99991231203007))
        let ruleDates = rule.between(startDt, endDt,true, (d,len) => {return (l == 0) ||  (len < limit)})
        return ruleDates
    }

    /**
     * @deprecated The method should not be used
     */
    static isRunDate(row: WB_ScheduleRow): boolean {
        if (_.isEmpty(row))
            return false

        if (row.cif.SCHEDULE_TYPE == 'once') {
            // @ts-ignore
            return new Date(row.ScheduleStartDtTs.setHours(0, 0, 0, 0)).toUTCString()== DateUtils.today.toUTCString()
        }

        if (_.includes(['daily', 'hourly'], row.cif.RULE_FREQUENCY))
            return true

        let rules: Date[] = this.getRules(row)

        if (_.filter(rules, (rule: Date) => new Date(rule.setHours(0, 0, 0, 0)).toUTCString() == DateUtils.today.toUTCString()).length)
            return true
        return false
    }

}
